import { useState } from "react";
import { Headphones, ExternalLink } from "lucide-react";
import { buildEventPayload, sendTrackingEvent } from "@/lib/tracker";

const PLATFORM_LABEL = {
  spotify:    "Spotify",
  apple:      "Apple Podcasts",
  youtube:    "YouTube Podcast",
  podbean:    "Podbean",
  buzzsprout: "Buzzsprout",
  amazon:     "Amazon Music",
  iheart:     "iHeart",
  other:      "Podcast",
};

const PLATFORM_TINT = {
  spotify:    "from-[#1DB954]/20 border-[#1DB954]/40",
  apple:      "from-[#A855F7]/20 border-[#A855F7]/40",
  youtube:    "from-[#EF4444]/20 border-[#EF4444]/40",
  podbean:    "from-[#F59E0B]/20 border-[#F59E0B]/40",
  buzzsprout: "from-[#EC4899]/20 border-[#EC4899]/40",
  amazon:     "from-[#00E5FF]/20 border-[#00E5FF]/40",
  iheart:     "from-[#EF4444]/20 border-[#EF4444]/40",
  other:      "from-[#7C3AED]/20 border-[#7C3AED]/40",
};

function titleByKind(listing) {
  if (listing?.listing_kind === "creator") return "Creator Podcast";
  const cat = (listing?.category_name || "").toLowerCase();
  if (listing?.is_individual || /agent|realtor|broker|loan|consultant|advisor|insurance/.test(cat)) return "Agent Podcast";
  return "Business Podcast";
}

export default function PodcastCard({ listing }) {
  const [clicked, setClicked] = useState(false);

  if (!listing?.podcast_url) return null;

  const platform = listing.podcast_platform || "other";
  const tint = PLATFORM_TINT[platform] || PLATFORM_TINT.other;
  const cover = listing.podcast_image || listing.logo_url;

  const onClick = () => {
    if (clicked) return;
    setClicked(true);
    try {
      const payload = buildEventPayload("podcast_click", listing);
      sendTrackingEvent(payload);
    } catch (_) { /* fire-and-forget */ }
  };

  return (
    <section className="mt-10" data-testid="podcast-card-section">
      <a
        href={listing.podcast_url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className={`relative block rounded-2xl border bg-gradient-to-br ${tint} to-[#0F172A] p-5 sm:p-6 overflow-hidden hover:scale-[1.005] transition group`}
        data-testid="podcast-card"
      >
        <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
          {cover ? (
            <img
              src={cover}
              alt={listing.podcast_title || "Podcast cover"}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover bg-white/10 flex-shrink-0 shadow-lg"
            />
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Headphones className="w-10 h-10 text-[#94A3B8]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mb-1">
              {titleByKind(listing)} · {PLATFORM_LABEL[platform] || "Podcast"}
            </p>
            <h3 className="text-white font-bold text-lg sm:text-xl leading-tight font-['Outfit']">
              {listing.podcast_title || `Listen to ${listing.listing_kind === "creator" ? "My" : "Our"} Podcast`}
            </h3>
            {listing.podcast_description && (
              <p className="text-sm text-[#CBD5E1] mt-2 line-clamp-3">{listing.podcast_description}</p>
            )}
            <span className="inline-flex mt-4 items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-[#FBBF24] to-[#EC4899] text-white text-sm font-semibold shadow-lg group-hover:shadow-[#EC4899]/30">
              <Headphones className="w-4 h-4" /> Listen Now <ExternalLink className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>
      </a>
    </section>
  );
}
